import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useBookmarks } from '../../contexts/BookmarkContext';
import { useTheme } from '../../contexts/ThemeContext';
import { getSearchSuggestions } from '../../utils/search';

const { FiSearch, FiX, FiFilter, FiCommand } = FiIcons;

const SearchBar = () => {
  const [focused, setFocused] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const inputRef = useRef(null);
  const { searchQuery, search, bookmarks, searchScope, setSearchScope } = useBookmarks();
  const { theme } = useTheme();

  useEffect(() => {
    if (searchQuery) {
      const newSuggestions = getSearchSuggestions(bookmarks, searchQuery);
      setSuggestions(newSuggestions);
    } else {
      setSuggestions([]);
    }
  }, [searchQuery, bookmarks]);

  const handleInputChange = (e) => {
    search(e.target.value);
  };

  const handleSuggestionClick = (suggestion) => {
    search(suggestion);
    setFocused(false);
    inputRef.current?.blur();
  };

  const clearSearch = () => {
    search('');
    inputRef.current?.focus();
  };

  const toggleScope = () => {
    setSearchScope(searchScope === 'all' ? 'current' : 'all');
  };

  return (
    <div className="relative w-full">
      <div 
        className={`group relative flex items-center rounded-2xl border transition-all duration-300 ${
          focused 
            ? 'border-indigo-500 bg-white ring-4 ring-indigo-500/10 shadow-lg' 
            : theme === 'dark' 
              ? 'border-zinc-800 bg-zinc-900/50 hover:border-zinc-700' 
              : 'border-gray-200 bg-gray-50 hover:border-gray-300'
        } ${focused && theme === 'dark' ? 'bg-zinc-900' : ''}`}
      >
        <button 
          onClick={toggleScope}
          className={`flex items-center px-4 py-2.5 border-r transition-all ${
            theme === 'dark' ? 'border-zinc-800 hover:bg-zinc-800' : 'border-gray-200 hover:bg-gray-100'
          } rounded-l-2xl`}
          title={`Scope: ${searchScope === 'all' ? 'Global' : 'Current Folder'}`}
        >
          <SafeIcon 
            icon={FiFilter} 
            className={`w-4 h-4 transition-colors ${searchScope === 'current' ? 'text-indigo-500' : 'text-zinc-500'}`} 
          />
          <span className="ml-2 text-[10px] font-black uppercase tracking-widest hidden md:block">
            {searchScope === 'all' ? 'All' : 'Local'}
          </span>
        </button>

        <SafeIcon 
          icon={FiSearch} 
          className={`w-5 h-5 ml-4 transition-colors ${
            focused ? 'text-indigo-500' : 'text-zinc-500'
          }`} 
        />
        
        <input 
          ref={inputRef}
          type="text" 
          placeholder="Search everything..." 
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 200)}
          className={`flex-1 px-4 py-3 bg-transparent focus:outline-none text-sm font-medium ${
            theme === 'dark' ? 'text-white placeholder-zinc-600' : 'text-zinc-900 placeholder-zinc-400'
          }`}
        />
        
        <div className="flex items-center px-3 space-x-2">
          {searchQuery ? (
            <button 
              onClick={clearSearch}
              className={`p-1.5 rounded-xl transition-all ${
                theme === 'dark' ? 'hover:bg-zinc-800' : 'hover:bg-gray-200'
              }`}
            >
              <SafeIcon icon={FiX} className="w-4 h-4 text-zinc-500" />
            </button>
          ) : (
            <div className={`hidden lg:flex items-center px-2 py-1 rounded-lg border text-[10px] font-black uppercase tracking-tighter ${
              theme === 'dark' ? 'border-zinc-800 text-zinc-600 bg-zinc-900' : 'border-gray-200 text-gray-400 bg-white'
            }`}>
              <SafeIcon icon={FiCommand} className="w-3 h-3 mr-1" />
              <span>K</span>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {focused && suggestions.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0, y: 10 }}
            className={`absolute top-full left-0 right-0 mt-2 rounded-2xl border shadow-2xl z-[60] overflow-hidden ${
              theme === 'dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-100'
            }`}
          >
            <div className="p-2">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500 px-3 py-2">Suggestions</p>
              {suggestions.map((suggestion, index) => (
                <button 
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className={`flex items-center w-full px-4 py-3 text-left text-sm font-semibold transition-all rounded-xl ${
                    theme === 'dark' ? 'hover:bg-zinc-800' : 'hover:bg-gray-50'
                  }`}
                >
                  <SafeIcon icon={FiSearch} className="w-4 h-4 mr-3 text-zinc-400" />
                  <span>{suggestion}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchBar;